import React, { useState } from 'react';
import { AuditRecord, TradePlan } from '../../../types';
import { exportTradePlansToCSV } from '../../../services/auditService';
import { Th, Td, tbStyle } from './AuditTablePrimitives';

const PLAN_STATUS_COLORS: Record<string, string> = {
  planned: 'var(--cyan)', taken: 'var(--green)', skipped: 'var(--t3)',
  expired: 'var(--t3)', invalidated: 'var(--red)',
};
const TIER_COLORS: Record<string, string> = {
  standard: 'var(--green)', light: 'var(--cyan)', reduced: 'var(--amber)', avoid: 'var(--red)',
};

function TradePlanEditor({ plan, onSave }: { plan: TradePlan; onSave: (p: TradePlan) => void }) {
  const [status,   setStatus]   = useState(plan.status);
  const [notes,    setNotes]    = useState(plan.notes);
  const [linkedId, setLinkedId] = useState(plan.linkedAuditId ?? '');

  const inp = { padding: '4px 8px', background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 4, color: 'var(--t1)', fontSize: 11, fontFamily: 'var(--mono)', width: '100%' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {[
          ['Entry Zone', plan.entryPlanned],
          ['Confirmation', plan.confirmationRule],
          ['Abort Rule', plan.abortRule],
          ['Management', plan.managementPlan],
          ['Stop Ref', plan.stopPlanned !== null ? String(plan.stopPlanned) : '—'],
          ['T1 Ref', plan.target1Planned !== null ? String(plan.target1Planned) : '—'],
        ].map(([label, val]) => (
          <div key={label}>
            <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</div>
            <div style={{ fontSize: 10, color: 'var(--t2)', lineHeight: 1.3 }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>STATUS</div>
          <select value={status} onChange={e => setStatus(e.target.value as any)} style={{ ...inp, width: 130 }}>
            {(['planned','taken','skipped','expired','invalidated'] as const).map(s =>
              <option key={s} value={s}>{s}</option>
            )}
          </select>
        </div>
        <div style={{ flex: '0 0 180px' }}>
          <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>LINKED AUDIT ID (optional)</div>
          <input value={linkedId} onChange={e => setLinkedId(e.target.value)} placeholder="audit record id…" style={inp} />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>NOTES</div>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes…" style={inp} />
        </div>
        <button onClick={() => onSave({ ...plan, status, notes, linkedAuditId: linkedId || null })}
          style={{ padding: '5px 14px', background: 'var(--cyan)20', border: '1px solid var(--cyan)',
            borderRadius: 4, color: 'var(--cyan)', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' }}>
          Save
        </button>
      </div>
    </div>
  );
}

export function TradePlansView({ plans, auditRecords, onUpdate, onDelete }: {
  plans: TradePlan[];
  auditRecords: AuditRecord[];
  onUpdate: (p: TradePlan) => void;
  onDelete: (id: string) => void;
}) {
  const [editId,     setEditId]     = useState<string | null>(null);
  const [filterSym,  setFilterSym]  = useState('');
  const [filterStat, setFilterStat] = useState('');

  const downloadCSV = () => {
    const blob = new Blob([exportTradePlansToCSV(plans)], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `mer-plans-${Date.now()}.csv`; a.click();
  };

  const filtered = plans
    .filter(p => !filterSym  || p.symbol.toLowerCase().includes(filterSym.toLowerCase()))
    .filter(p => !filterStat || p.status === filterStat)
    .sort((a, b) => b.createdAt - a.createdAt);

  const btnS = (active: boolean) => ({
    padding: '3px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 10,
    fontFamily: 'var(--mono)', background: active ? 'var(--cyan)20' : 'var(--surface)',
    border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
    color: active ? 'var(--cyan)' : 'var(--t2)',
  });

  if (!plans.length) {
    return (
      <div style={{ padding: 24, color: 'var(--t3)', textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>No trade plans yet</div>
        <div style={{ fontSize: 12, color: 'var(--t3)', maxWidth: 400, margin: '0 auto' }}>
          Click <strong>📋 Save Plan</strong> in the AI Thesis panel when a setup is active.
          Plans are descriptive decision-support records — not orders or recommendations.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={filterSym} onChange={e => setFilterSym(e.target.value)}
          placeholder="Filter symbol…" style={{ padding: '4px 8px', background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: 4, color: 'var(--t1)',
            fontSize: 11, fontFamily: 'var(--mono)', width: 110 }} />
        <select value={filterStat} onChange={e => setFilterStat(e.target.value)}
          style={{ padding: '4px 8px', background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 4, color: 'var(--t2)', fontSize: 11, fontFamily: 'var(--mono)' }}>
          <option value="">All statuses</option>
          {['planned','taken','skipped','expired','invalidated'].map(s =>
            <option key={s} value={s}>{s}</option>
          )}
        </select>
        <span style={{ fontSize: 10, color: 'var(--t3)', marginLeft: 'auto' }}>{filtered.length} plans</span>
        <button onClick={downloadCSV} style={btnS(false)}>↓ CSV</button>
      </div>

      <table style={tbStyle}>
        <thead>
          <tr>
            <Th>Symbol</Th><Th>Setup</Th><Th>Dir</Th><Th>Bias</Th>
            <Th right>Conv</Th><Th>Phase</Th><Th>Risk Tier</Th>
            <Th>Status</Th><Th>Created</Th>
            <th style={{ padding: '5px 10px', borderBottom: '1px solid var(--border)' }}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(p => (
            <React.Fragment key={p.id}>
              <tr style={{ background: editId === p.id ? 'var(--surface2)' : 'transparent' }}>
                <Td><span style={{ fontWeight: 700 }}>{p.symbol}</span></Td>
                <Td><span style={{ fontSize: 9 }}>{p.setupType.substring(0, 20)}</span></Td>
                <Td>
                  <span style={{ fontSize: 9, fontWeight: 700,
                    color: p.direction === 'long' ? 'var(--green)' : 'var(--red)' }}>
                    {p.direction.toUpperCase()}
                  </span>
                </Td>
                <Td>
                  <span style={{ fontSize: 9, color: p.bias === 'bullish' ? 'var(--green)' : p.bias === 'bearish' ? 'var(--red)' : 'var(--t3)' }}>
                    {p.bias}
                  </span>
                </Td>
                <Td right color={p.conviction >= 65 ? 'var(--green)' : p.conviction >= 45 ? 'var(--amber)' : 'var(--t3)'}>
                  {p.conviction}%
                </Td>
                <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{p.phase}</span></Td>
                <Td>
                  <span style={{ fontSize: 9, fontWeight: 700, color: TIER_COLORS[p.riskTierSuggested] ?? 'var(--t2)' }}>
                    {p.riskTierSuggested.toUpperCase()}
                  </span>
                </Td>
                <Td>
                  <span style={{ fontSize: 9, color: PLAN_STATUS_COLORS[p.status] ?? 'var(--t2)' }}>
                    {p.status}
                  </span>
                </Td>
                <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{new Date(p.createdAt).toLocaleTimeString()}</span></Td>
                <Td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => setEditId(editId === p.id ? null : p.id)}
                      style={{ ...btnS(editId === p.id), fontSize: 9 }}>
                      {editId === p.id ? 'Close' : 'Edit'}
                    </button>
                    <button onClick={() => onDelete(p.id)}
                      style={{ ...btnS(false), fontSize: 9, color: 'var(--red)' }}>✕</button>
                  </div>
                </Td>
              </tr>
              {editId === p.id && (
                <tr>
                  <td colSpan={10} style={{ padding: '8px 10px', background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)' }}>
                    <TradePlanEditor plan={p} onSave={updated => { onUpdate(updated); setEditId(null); }} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div style={{ fontSize: 9, color: 'var(--t3)', fontStyle: 'italic' }}>
        Trade plans are descriptive decision-support records. They do not constitute investment advice,
        do not execute trades, and do not connect to any brokerage. Risk tier labels are analytical
        context, not position-sizing recommendations.
      </div>
    </div>
  );
}
